import Vue from 'vue';
import { serialize } from 'cookie';
import clearTimeFromDate from '@/utils/clear-time-from-date';
import { getGeoCoords } from '@/utils/geo-utils';
import { numToFixed } from '@/utils/number-utils';
import { calcDistanceByLocations } from '@/utils/distance-utils';

const clearTime = promo =>
  promo
    .map(promo => ({
      ...promo,
      dateBegin: clearTimeFromDate(new Date(promo.dateBegin)).toISOString(),
      dateEnd: clearTimeFromDate(new Date(promo.dateEnd)).toISOString(),
    }));

const sortByBegin = promo =>
  promo
    .sort((a, b) => {
      return new Date(b.dateBegin) - new Date(a.dateBegin);
    });

export default {
  namespaced: true,

  state: () => ({
    city: {
      // По дефолту москва, для первого ssr
      id: '1720920299',
      name: "Москва",
      region: {},
      geo: {},
      hasMetro: true,
    },
    cityConfirmed: false,
    account: {},
    addresses: [],
    promos: [],
    coupons: [],
    geoCoords: null,
  }),

  getters: {
    city: state => state.city,
    cityId: state => state.city.id,
    cityConfirmed: state => state.cityConfirmed,
    account: state => state.account,
    accountCity: (state ) => state.account.city || {},
    loggedIn: state => !!state.account.id,
    confirmedEmail: state => state.account.confirmedEmail,
    isEmailConfirmed: state => state.account.emailConfirmed,
    unconfirmedEmail: state => state.account.unconfirmedEmail,
    needToConfirmNew: (state, getters) => {
      if (!getters.confirmedEmail || !getters.unconfirmedEmail)
        return false;

      return getters.account.email !== getters.unconfirmedEmail;
    },
    addresses: state => state.addresses,
    subscriptions: state => state.account.subscriptions || [],
    promos: state => state.promos,
    promosCount: state => state.promos.length,
    coupons: state => state.coupons,
    couponsCount: state => state.coupons.length,
    cashbackBonus: state => state.account.bonuses?.cashback || {},
    userBonusCardNumber: state => state.account.bonus?.cardNumber,
    promoBonusesAtDate: state => date => {
      const argDate = clearTimeFromDate(date);

      const promoTotal = state.promos
        .filter(promo => {
          const promoStart = clearTimeFromDate(new Date(promo.dateBegin));
          const promoEnd = clearTimeFromDate(new Date(promo.dateEnd));

          return argDate >= promoStart && promoEnd >= argDate
            && promo.bonusType === "action";
        })
        .reduce((acc, { bonuses }) => acc + parseInt(bonuses), 0);

      return promoTotal;
    },
    bonuses: state => Math.floor(state.account?.bonuses?.general?.amount || 0),
    // sort мутирует и вызывает бесконечный цикл рендера по этому создаем новый массив
    promosSortByEnd: (state, getters) => [...getters.promos].sort((a, b) => {
      return new Date(a.dateEnd) - new Date(b.dateEnd);
    }),
    calcDistanceToUser: (state) => (coords) => state.geoCoords && coords
      ? numToFixed(calcDistanceByLocations(state.geoCoords, coords), 1)
      : '',
  },

  mutations: {
    setCity: (state, city = {}) => {
      state.city = city;
    },
    setCityId: (state, id) => {
      Vue.set(state.city, 'id', id);
    },
    cityConfirmed: (state, confirmed = true) => {
      state.cityConfirmed = confirmed;
    },
    setAccount: (state, account) => {
      const phone = account.phone || '';

      state.account = { ...account, phone: phone.replace(/\D/g,'') };
    },
    setAddresses: (state, addresses) => {
      state.addresses = addresses;
    },
    setSubscriptions: (state, subs) => {
      state.account.subscriptions = subs;
    },
    setPromos(state, promos) {
      state.promos = promos;
    },
    setCoupons(state, coupons) {
      state.coupons = coupons;
    },
    setEmailData(state, { unconfirmedEmail, confirmedEmail, emailConfirmed, email}) {
      state.account = {...state.account, unconfirmedEmail, confirmedEmail, emailConfirmed, email};
    },
    setGeoCoords: (state, coords) => state.geoCoords = coords,
  },

  actions: {
    updateCity({ commit, dispatch }, city = {}) {
      commit('setCity', city);
      dispatch('setClientCookie');
    },

    detectCity({ dispatch }) {
      // вызываем только на клиенте, тк нужен ip
      dispatch('setClientCookie'); // ставим дефолт, если метод умрет
      return this.$api.detectCity()
        .then(city => {
          dispatch('updateCity', city);
        })
        .catch(() => {});
    },

    getCity({ commit }) {
      return this.$api.getCities().then(([city]) => {
        if (city && city.id) commit('setCity', city);
        // todo если несуществующий город, надо сбросить все
        // https://jira.app.local/browse/ESMFUN-676
      }).catch(() => {});
    },

    loadAddresses({ commit }) {
      return this.$api.getAddresses()
          .then(addresses => {
            commit('setAddresses', addresses)
          })
    },

    setClientCookie({ state }) {
      const cookieOptions = {
        maxAge: 31556952000, // 1Y
        path: '/',
        sameSite: 'lax'
      };
      document.cookie = serialize('CITY_ID', state.city.id, cookieOptions);
    },

    setSubscriptions({commit}, subs = []) {
      commit('setSubscriptions', subs);
    },

    register({ commit, dispatch, getters }, {
      name = '',
      cityId = getters.cityId,
      token = '',
      email = '',
      subscription = true,
      phone = '',
    } = {}) {
      return this.$api.register({
        name, cityId, token, email, subscription, phone
      }).then(account => {
        commit('setAccount', account);
        dispatch('onLogin');
        return account;
      });
    },

    login({ dispatch, commit }, { type = 'sms', value = '', token = ''} = {}) {
      return this.$api.login({
        type,
        value,
        token,
      }).then(account => {
        commit('setAccount', account);
        dispatch('onLogin');
        return account;
      });
    },

    onLogin({ dispatch }) {
      dispatch('cart/loadCartAfterLogin', null, { root: true });
      dispatch('favorites/loadFavoritesShort', null, { root: true });
    },

    getAccount({ commit }) {
      // Первый запрос с бека, отрабатывает для перевыпуска токена!
      return this.$api.getAccount().then(account => {
        commit('setAccount', account);
      }).catch(e => {
        /* протухший токен */
        if (e.response?.status === 400) throw e;
      });
    },

    updateProfile({ commit }, form) {
      return this.$api.updateAccount({ form }).then(account => {
        commit('setAccount', account);
      });
    },

    loadCoupons({commit}) {
      return this.$api.getCoupons().then(coupons => {
        commit('setCoupons', sortByBegin(clearTime(coupons)));
      })
      .catch(() => {
        console.warn('Не удалось загрузить купоны')
      });
    },

    loadPromos({commit}) {
      return this.$api.getPromotions().then(promotions => {
        commit('setPromos', sortByBegin(clearTime(promotions)));
      })
      .catch(() => {
        console.warn('Не удалось загрузить акции');
      });
    },

    async sendConfirmEmail({ dispatch }, { email }) {
      try {
        await this.$api.sendConfirmEmail({ email });

        dispatch('notifications/create', {
          type: 'success',
          title: 'Успешно!',
          message: 'Письмо для подтверждения Email успешно отправлено',
        }, { root: true });
      } catch(e) {
        dispatch('notifications/create', {
          type: 'error',
          title: 'Ошибка',
          message: 'При выполнении запроса произошла ошибка',
        }, { root: true });

        return Promise.reject(e);
      }
    },

    async changeEmail({ commit }, newEmail) {
      try {
        const { status, unconfirmedEmail, confirmedEmail, emailConfirmed, email } = await this.$api.changeEmail(newEmail);

        if ( status === 400) {
          return Promise.reject('Введённый Email уже существует в системе');
        }

        commit('setEmailData', { unconfirmedEmail, confirmedEmail, emailConfirmed, email });
      } catch(e) {
        console.error(e);
        return Promise.reject(e?.errors?.[0]?.title || '');
      }
    },

    async getGeoCoords({ state, commit }) {
      try {
        const coords = await getGeoCoords();
        commit(
          'setGeoCoords',
          coords ? { latitude: coords.latitude, longitude: coords.longitude } : null
        );
      } catch (e) {
        commit('setGeoCoords', null);
      }

      return state.geoCoords;
    }
  }
}
